import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { BiPlus, BiTrash, BiSave, BiLinkAlt, BiDownload, BiUpload, BiCalendarPlus, BiEraser } from "react-icons/bi";

import { getJrLineDetail, saveJrLineDetail, getRoomOccupancy } from "../job-request/jrV2Slice";
import { downloadTemplate as dlTemplate, parseEntriesFromExcel } from "./entryImport";
import QtyInput, { formatQty } from "./QtyInput";
import FotoBuktiLink, { adaFotoAkomodasi } from "./FotoBukti";

const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
const fmt = (n) => new Intl.NumberFormat("id-ID").format(Number(n) || 0);
// Tanggal lokal (bukan toISOString/UTC yang mundur sehari sebelum jam 07/08 pagi).
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
// Default "Tanggal" baris baru = tanggal kerja JR: hari ini bila masih di bulan
// kerja, selain itu tanggal kerja JR (MJS merekap per bulan kerja, bukan tanggal input).
const defaultEntryDate = (periodMonth, workDate) => {
  if (!periodMonth || today().startsWith(periodMonth)) return today();
  return String(workDate || "").startsWith(periodMonth) ? String(workDate).slice(0, 10) : periodMonth + "-01";
};
const thisMonth = () => today().slice(0, 7);

// Semua tanggal (YYYY-MM-DD) dalam satu bulan "YYYY-MM".
// Dirakit sebagai string, bukan lewat toISOString(), supaya tidak bergeser
// mundur sehari karena zona waktu.
function monthDates(ym) {
  const [y, m] = String(ym || "").split("-").map(Number);
  if (!y || !m || m < 1 || m > 12) return [];
  const lastDay = new Date(y, m, 0).getDate(); // hari ke-0 bulan berikutnya
  return Array.from({ length: lastDay }, (_, i) =>
    `${ym}-${String(i + 1).padStart(2, "0")}`);
}

// "2026-08" → "Agustus 2026". Tanggal 1 dipakai sebagai jangkar agar bulan
// tak bergeser di zona waktu mana pun.
function monthLabel(ym) {
  const [y, m] = String(ym || "").split("-").map(Number);
  if (!y || !m) return ym || "";
  return new Date(y, m - 1, 1).toLocaleDateString("id-ID", { month: "long", year: "numeric" });
}

// Konfigurasi kolom final per sub-tipe (selaras backend formulaTypeForSubType).
// type: date | number | link | check | uom(read-only) | label(read-only dari item)
const KET = { key: "Remarks", label: "Keterangan", type: "text" };

export const GS_FORM = {
  MEALS: {
    formula: "QTY",
    cols: [
      { key: "EntryDate",   label: "Tanggal",   type: "date" },
      { key: "RoomRef",     label: "Room",      type: "roomselect" },
      { key: "Qty",         label: "Qty",       type: "number" },
      { key: "Uom",         label: "UoM",       type: "uom" },
      { key: "EvidenceUrl", label: "Link Foto", type: "link" },
      KET,
    ],
  },
  ROOM: {
    formula: "PER_DATE",
    cols: [
      { key: "RoomRef",      label: "Room",          type: "roomselect" },
      { key: "EntryDate",    label: "Tanggal",       type: "date" },
      { key: "Uom",          label: "UoM",           type: "uom" },
      { key: "OccupantName", label: "Nama Penghuni", type: "text" },
      { key: "HouseKeeping", label: "House Keeping", type: "check" },
      { key: "EvidenceUrl",  label: "Link Bukti",    type: "link" },
      KET,
    ],
  },
  LAUNDRY: {
    formula: "QTY",
    cols: [
      { key: "_item",       label: "Jenis Bahan", type: "label" },
      { key: "RoomRef",     label: "Room",        type: "roomselect" },
      { key: "EntryDate",   label: "Tanggal",     type: "date" },
      { key: "Qty",         label: "Qty",         type: "number" },
      { key: "Uom",         label: "UoM",         type: "uom" },
      { key: "EvidenceUrl", label: "Link Bukti",  type: "link" },
      KET,
    ],
  },
  DRILL: {
    formula: "METER",
    cols: [
      { key: "EntryDate",  label: "Tanggal",     type: "date" },
      { key: "MeterStart", label: "Meter Awal",  type: "number" },
      { key: "MeterEnd",   label: "Meter Akhir", type: "number" },
      KET,
    ],
  },
  WOODEN:  { formula: "QTY", cols: [{ key: "EntryDate", label: "Tanggal", type: "date" }, { key: "Qty", label: "Qty",  type: "number" }, KET] },
  VEHICLE: { formula: "QTY", cols: [{ key: "EntryDate", label: "Tanggal", type: "date" }, { key: "Qty", label: "Trip", type: "number" }, KET] },
  OTHERS:  { formula: "QTY", cols: [{ key: "EntryDate", label: "Tanggal", type: "date" }, { key: "Qty", label: "Qty",  type: "number" }, KET] },
};
const DEFAULT_FORM = GS_FORM.OTHERS;

// Hitung "jumlah" (BilledQty) — mirror backend computeBilledQty.
export function calcBilled(formula, e) {
  switch (formula) {
    case "PER_DATE": return 1; // ROOM: 1 malam per tanggal
    case "METER": { const d = num(e.MeterEnd) - num(e.MeterStart); return d > 0 ? d : 0; }
    case "QTY_X_MULT": return num(e.Qty) * (num(e.Multiplier) || 1);
    default: return num(e.Qty);
  }
}

function blankRow(entryDate = today()) {
  return { EntryDate: entryDate, Qty: "", EvidenceUrl: "", HouseKeeping: false, OccupantName: "", RoomRef: "", Remarks: "" };
}

export default function GeneralServiceEntryEditor({ line, jrID, roomOptions = [], period, workDate, onSaved }) {
  const dispatch = useDispatch();
  const cfg = GS_FORM[line.SubType] || DEFAULT_FORM;
  const itemLabel = line.ItemDisplay || line.ItemName || "—";
  // Kolom tanggal form ini (semua sub-tipe GS memakainya, tapi jangan diasumsikan).
  const dateKey = (cfg.cols.find(c => c.type === "date") || {}).key;

  const [entries, setEntries] = useState([]);
  // Bulan yang tanggalnya dibuatkan baris. Kalau JR-nya punya Period (JrType=GS
  // selalu punya), bulan itulah yang berlaku dan TIDAK boleh dipilih bebas —
  // entri di luar periode akan ikut tertagih pada periode yang salah. Pilihan
  // bebas hanya disediakan sebagai fallback untuk JR tanpa Period.
  const periodMonth = /^\d{4}-\d{2}$/.test((period || "").slice(0, 7)) ? period.slice(0, 7) : "";
  const [genMonth, setGenMonth] = useState(() => periodMonth || thisMonth());
  const effMonth = periodMonth || genMonth;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [occupancy, setOccupancy] = useState([]); // [{RoomRef, EntryDate, OccupantName, JrID}]

  // Baris berisi "Room All" berarti kamar belum ditentukan — pilih dari kamar
  // yang masih kosong. Baris dengan kamar spesifik (mis. "Room Mess 1 Person")
  // dan layanan yang hanya DIANTAR ke kamar (Meals/Laundry) tidak disaring:
  // makanan justru diantar ke kamar yang sedang dihuni.
  const isRoomAll = /room\s*all/i.test(line.ItemDisplay || "") && line.SubType === "ROOM";

  useEffect(() => {
    if (!isRoomAll) return;
    dispatch(getRoomOccupancy(line.JrLineID)).unwrap()
      .then(res => setOccupancy(res?.data ?? []))
      .catch(() => setOccupancy([]));
  }, [dispatch, line.JrLineID, isRoomAll]);

  // Kamar yang boleh dipilih untuk SATU baris entri: buang yang sudah terpakai
  // pada tanggal baris itu. Kamar yang sedang dipilih baris ini sendiri tetap
  // disertakan agar nilainya tidak hilang dari dropdown.
  const roomsFor = (entry) => {
    if (!isRoomAll) return roomOptions;
    const tgl = entry?.EntryDate;
    if (!tgl) return roomOptions;
    const terpakai = new Set(
      occupancy.filter(o => o.EntryDate === tgl).map(o => o.RoomRef)
    );
    return roomOptions.filter(r => {
      const val = typeof r === "object" ? r.value : r;
      return !terpakai.has(val) || val === entry.RoomRef;
    });
  };

  const load = useCallback(() => {
    setLoading(true);
    dispatch(getJrLineDetail(line.JrLineID)).unwrap()
      .then(res => setEntries((res?.data ?? []).map(d => ({ ...d }))))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [dispatch, line.JrLineID]);

  useEffect(() => { load(); }, [load]);

  const addRow = () => setEntries(p => [...p, blankRow(defaultEntryDate(periodMonth, workDate))]);
  const delRow = (i) => setEntries(p => p.filter((_, idx) => idx !== i));
  const upd = (i, field, val) =>
    setEntries(p => p.map((e, idx) => idx === i ? { ...e, [field]: val } : e));

  // Buat satu baris per tanggal dalam sebulan supaya user tinggal mengisi Qty.
  // Tanggal yang barisnya sudah ada dilewati agar entri lama tidak terduplikasi.
  const generateMonth = () => {
    const dates = monthDates(effMonth);
    if (!dates.length) { toast.error("Pilih bulan dulu"); return; }
    const sudahAda = new Set(entries.map(e => e[dateKey]).filter(Boolean));
    const baru = dates.filter(d => !sudahAda.has(d));
    if (!baru.length) {
      toast.info("Semua tanggal di bulan itu sudah punya baris");
      return;
    }
    // ROOM ditagih per tanggal (1 malam per baris), jadi sebulan penuh langsung
    // menambah tagihan — beda dengan sub-tipe lain yang baris kosongnya bernilai 0.
    if (cfg.formula === "PER_DATE" && !window.confirm(
      `${baru.length} baris tanggal akan dibuat untuk ${itemLabel}.\n\n` +
      `Perhatian: satu baris = satu malam, jadi ini langsung menghitung ${baru.length} malam. ` +
      `Hapus tanggal yang tidak terpakai sebelum menyimpan.`
    )) return;

    setEntries(p => [...p, ...baru.map(d => ({ ...blankRow(), [dateKey]: d }))]
      .sort((a, b) => String(a[dateKey] ?? "").localeCompare(String(b[dateKey] ?? ""))));
    toast.success(`${baru.length} tanggal ditambahkan — tinggal isi ${cfg.formula === "METER" ? "meter" : "qty"}`);
  };

  // Baris hasil generate yang tidak jadi dipakai: belum ada angka & tanpa catatan.
  const barisKosong = (e) => {
    const adaCatatan = !!(e.Remarks || e.EvidenceUrl);
    if (cfg.formula === "PER_DATE") return !e.RoomRef && !e.OccupantName && !adaCatatan;
    return calcBilled(cfg.formula, e) === 0 && !adaCatatan;
  };
  const jumlahKosong = entries.filter(barisKosong).length;
  const hapusBarisKosong = () => {
    setEntries(p => p.filter(e => !barisKosong(e)));
    toast.success(`${jumlahKosong} baris kosong dihapus`);
  };

  const fileRef = useRef(null);
  const downloadTemplate = () => dlTemplate(cfg, itemLabel);
  const importExcel = (file) =>
    parseEntriesFromExcel(file, cfg, () => blankRow(defaultEntryDate(periodMonth, workDate)))
      .then(rows => { setEntries(rows); toast.success(`${rows.length} baris diimpor — periksa lalu klik Simpan Detail`); })
      .catch(err => toast.error("Gagal impor: " + (err?.message ?? err)));

  const total = entries.reduce((s, e) => s + calcBilled(cfg.formula, e), 0);
  const subtotal = total * num(line.ContractRate);

  const save = async () => {
    // Entri di luar bulan kerja JR tetap direkap MJS di bulan kerja, tapi
    // kemungkinan besar tanggalnya salah ketik / tertinggal default.
    const luar = periodMonth ? entries.filter(e => {
      const t = String(e.EntryDate || e.DateStart || "").slice(0, 7);
      return t && t !== periodMonth;
    }).length : 0;
    if (luar && !window.confirm(
      `${luar} baris bertanggal di luar bulan kerja JR (${periodMonth}).\n\nTetap simpan?`
    )) return;
    setSaving(true);
    // Backend mengharapkan tipe numerik; input form menyimpan string → konversi dulu.
    const numOrNull = (v) => (v === "" || v == null || isNaN(parseFloat(v)) ? null : parseFloat(v));
    const payload = entries.map(e => ({
      ...e,
      Qty: num(e.Qty),
      Multiplier: (e.Multiplier === "" || e.Multiplier == null) ? 1 : num(e.Multiplier),
      MeterStart: numOrNull(e.MeterStart),
      MeterEnd: numOrNull(e.MeterEnd),
    }));
    try {
      await dispatch(saveJrLineDetail({ JrID: jrID, JrLineID: line.JrLineID, Entries: payload })).unwrap();
      toast.success("Detail aktivitas tersimpan");
      onSaved && onSaved();
      load();
    } catch (e) {
      toast.error(typeof e === "string" ? e : (e?.message ?? "Gagal simpan detail"));
    } finally { setSaving(false); }
  };

  // Render satu sel sesuai tipe kolom
  const renderCell = (col, e, i) => {
    switch (col.type) {
      case "label":
        return <span className="text-sm font-medium text-gray-700">{itemLabel}</span>;
      case "uom":
        return <span className="text-sm text-gray-500">{line.SatuanNama || line.SatuanID || "—"}</span>;
      case "check":
        return (
          <input type="checkbox" className="w-4 h-4"
            checked={!!e.HouseKeeping}
            onChange={ev => upd(i, "HouseKeeping", ev.target.checked)} />
        );
      case "text":
        return (
          <input type="text" value={e[col.key] ?? ""}
            onChange={ev => upd(i, col.key, ev.target.value)}
            className="w-40 border rounded px-2 py-1 text-sm outline-none focus:border-purple-400" />
        );
      case "roomselect": {
        const tersedia = roomsFor(e);
        return (
          <select value={e.RoomRef ?? ""}
            onChange={ev => upd(i, "RoomRef", ev.target.value)}
            className="w-40 border rounded px-2 py-1 text-sm outline-none focus:border-purple-400 bg-white">
            <option value="">
              {isRoomAll && e.EntryDate && tersedia.length === 0
                ? "— semua kamar terisi —"
                : "— pilih —"}
            </option>
            {tersedia.map(r => (
              typeof r === "object"
                ? <option key={r.value} value={r.value}>{r.label}</option>
                : <option key={r} value={r}>{r}</option>
            ))}
          </select>
        );
      }
      case "link":
        // Foto dari menu Akomodasi menggantikan link bukti: tombol Foto (n), gambar tampil saat diklik.
        if (adaFotoAkomodasi(e.EvidenceUrl)) return <FotoBuktiLink value={e.EvidenceUrl} />;
        return (
          <div className="flex items-center gap-1">
            <input type="url" placeholder="https://…" value={e.EvidenceUrl ?? ""}
              onChange={ev => upd(i, "EvidenceUrl", ev.target.value)}
              className="w-44 border rounded px-2 py-1 text-sm outline-none focus:border-purple-400" />
            {e.EvidenceUrl ? (
              <a href={e.EvidenceUrl} target="_blank" rel="noreferrer"
                className="text-purple-500 hover:text-purple-700"><BiLinkAlt /></a>
            ) : null}
          </div>
        );
      case "date":
        return (
          <input type="date" value={e[col.key] ?? ""}
            onChange={ev => upd(i, col.key, ev.target.value)}
            className="w-40 border rounded px-2 py-1 text-sm outline-none focus:border-purple-400" />
        );
      default: // number
        return (
          <QtyInput value={e[col.key] ?? ""}
            onChange={v => upd(i, col.key, v)}
            className="w-28 border rounded px-2 py-1 text-right text-sm outline-none focus:border-purple-400" />
        );
    }
  };

  const wasRejected = !!line.RejectReason;

  return (
    <div className={`rounded-xl border p-4 ${wasRejected ? "border-red-300 bg-red-50/40" : "border-purple-100 bg-purple-50/30"}`}>
      {wasRejected && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-red-100 border border-red-200 text-xs text-red-700">
          <span className="font-semibold">Sebelumnya ditolak klien — perlu revisi.</span> Alasan: {line.RejectReason}
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className={`font-semibold text-sm ${wasRejected ? "text-red-700" : "text-gray-800"}`}>{itemLabel}</div>
          <div className="text-xs text-gray-500">
            {line.SubType} · Rate {fmt(line.ContractRate)}/{line.SatuanNama || line.SatuanID}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {dateKey && (
            <div className="flex items-center gap-1 pr-2 mr-1 border-r border-purple-200">
              {periodMonth ? (
                <span title={`Periode JR ${periodMonth} — tidak bisa diubah di sini`}
                  className="border border-gray-200 bg-gray-100 text-gray-600 rounded-lg px-2 py-1 text-xs font-medium">
                  {monthLabel(periodMonth)}
                </span>
              ) : (
                <input type="month" value={genMonth}
                  onChange={ev => setGenMonth(ev.target.value)}
                  title="Bulan yang tanggalnya akan dibuatkan baris"
                  className="border rounded-lg px-2 py-1 text-xs outline-none focus:border-purple-400" />
              )}
              <button onClick={generateMonth}
                title={periodMonth
                  ? `Buat satu baris untuk setiap tanggal di ${monthLabel(periodMonth)} — tinggal isi qty`
                  : "Buat satu baris untuk setiap tanggal di bulan ini — tinggal isi qty"}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-purple-400 bg-purple-100 text-purple-700 text-xs font-semibold hover:bg-purple-200">
                <BiCalendarPlus /> Isi 1 Bulan
              </button>
              {jumlahKosong > 0 && (
                <button onClick={hapusBarisKosong}
                  title="Hapus baris tanggal yang belum diisi"
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-600 text-xs font-medium hover:bg-gray-50">
                  <BiEraser /> Hapus Kosong ({jumlahKosong})
                </button>
              )}
            </div>
          )}
          <button onClick={downloadTemplate} title="Unduh template Excel sesuai format form ini"
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-600 text-xs font-medium hover:bg-gray-50">
            <BiDownload /> Template
          </button>
          <button onClick={() => fileRef.current?.click()} title="Impor entri dari Excel"
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-green-300 text-green-700 text-xs font-medium hover:bg-green-50">
            <BiUpload /> Import
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden"
            onChange={ev => { const f = ev.target.files?.[0]; if (f) importExcel(f); ev.target.value = ""; }} />
          <button onClick={addRow}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-purple-300 text-purple-700 text-xs font-medium hover:bg-purple-50">
            <BiPlus /> Tambah Entri
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-gray-400 py-3">Memuat entri…</div>
      ) : entries.length === 0 ? (
        <div className="text-xs text-gray-400 py-3">
          Belum ada entri. Klik “Isi 1 Bulan” untuk membuat baris tiap tanggal
          {periodMonth ? ` di ${monthLabel(periodMonth)}` : ""}, atau “Tambah Entri” untuk satu baris.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500">
                {cfg.cols.map(c => <th key={c.key} className="px-2 py-1 font-semibold">{c.label}</th>)}
                <th className="px-2 py-1 font-semibold text-right">Jumlah</th>
                <th className="px-2 py-1" />
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={i} className="border-t border-purple-100">
                  {cfg.cols.map(c => (
                    <td key={c.key} className="px-2 py-1.5">{renderCell(c, e, i)}</td>
                  ))}
                  <td className="px-2 py-1.5 text-right font-semibold text-gray-800">
                    {formatQty(calcBilled(cfg.formula, e))}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <button onClick={() => delRow(i)}
                      className="text-red-400 hover:text-red-600 p-1"><BiTrash /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-purple-100">
        <div className="text-sm text-gray-600">
          Σ Jumlah <strong className="text-gray-900">{formatQty(total)}</strong>
          <span className="mx-2 text-gray-300">×</span>
          {fmt(line.ContractRate)}
          <span className="mx-2 text-gray-300">=</span>
          <strong className="text-purple-700">Rp {fmt(subtotal)}</strong>
        </div>
        {/* Tombol simpan sengaja HIJAU, bukan ungu: kartu General Service sendiri
            bernuansa ungu, jadi tombol ungu nyaris melebur dengan latarnya. */}
        <button onClick={save} disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-sm font-bold uppercase tracking-wide
                     bg-emerald-600 hover:bg-emerald-700 shadow-md shadow-emerald-600/30 ring-2 ring-emerald-600/20
                     focus:outline-none focus:ring-4 focus:ring-emerald-500/40
                     disabled:bg-gray-400 disabled:shadow-none disabled:ring-0 disabled:cursor-not-allowed">
          <BiSave className="text-lg" /> {saving ? "Menyimpan…" : "Simpan Detail"}
        </button>
      </div>
    </div>
  );
}
